import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CAlert,
    CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
    CForm, CRow, CCol, CFormInput, CFormCheck
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DataTableComponent from "components/DataTableComponent";
import AlertNotification from 'components/globals/AlertNotification';

const SCORE_OPTIONS = [
    ["Insatisfatório", 0],
    ["Pouco Satisfatorio", 1],
    ["Satisfatorio", 2],
    ["Bom", 3],
    ["Excelente", 4],
];

const SITUATION_OPTIONS = ["Potencial Fornecedor", "Novo Fornecedor", "Fornecedor"];

const todayISO = () => {
    const d = new Date();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
};

const DEFAULT_WEIGHT_CONFIGS = {
    2020: {
        year: 2020,
        price: 25,
        answer_capacity: 25,
        deadlines: 25,
        technical_capacity: 25,
        esg: 0,
    },
    2025: {
        year: 2025,
        price: 20,
        answer_capacity: 30,
        deadlines: 20,
        technical_capacity: 20,
        esg: 10,
    },
};

const getDefaultWeightConfigForYear = (year) =>
    Number(year) >= 2025 ? DEFAULT_WEIGHT_CONFIGS[2025] : DEFAULT_WEIGHT_CONFIGS[2020];

const getWeightConfig = (weightConfigs, year) => {
    const candidates = (weightConfigs || [])
        .filter((row) => Number(row?.year) <= Number(year))
        .sort((a, b) => Number(b.year) - Number(a.year));
    const config = candidates[0];
    return config || getDefaultWeightConfigForYear(year);
};

const computeAveragePct = (
    { price, answer_capacity, deadlines, technical_capacity, esg },
    year,
    weightConfigs
) => {
    const config = getWeightConfig(weightConfigs, year);
    const scoreValues = { price, answer_capacity, deadlines, technical_capacity, esg };
    const fields = ["price", "answer_capacity", "deadlines", "technical_capacity", "esg"]
        .filter((field) => (Number(config?.[field]) || 0) > 0);

    const values = fields.map((field) => ({ value: scoreValues[field], weight: Number(config?.[field]) || 0 }));
    if (values.some(({ value }) => typeof value !== "number")) return null;

    const totalWeight = values.reduce((sum, entry) => sum + entry.weight, 0);
    if (!totalWeight) return null;

    const weightedSum = values.reduce((sum, entry) => sum + entry.value * entry.weight, 0);
    return (weightedSum / totalWeight / 4) * 100;
};

const computeQualification = (situation, average /* number | null */) => {
    if (situation === "Novo Fornecedor") return "Processo de Qualificação";
    if (situation === "Potencial Fornecedor") return "Em processo de Qualificação";
    // Fornecedor -> depends on average
    if (typeof average !== "number") return ""; // no score yet
    if (average < 40) return "Não Qualificado";
    if (average < 60) return "Qualificado com restrições";
    return "Qualificado";
};

const toFiniteNumber = (value) => {
    const num = typeof value === "number" ? value : Number(value);
    return Number.isFinite(num) ? num : null;
};

const computeYearAverages = (rows) => {
    const evaluations = (rows || [])
        .map((row) => {
            const average = toFiniteNumber(row?.evaluation?.average);
            if (average === null) return null;

            return {
                average,
                isCritical: Boolean(row?.evaluation?.supplier_critical ?? row?.critical ?? false),
            };
        })
        .filter(Boolean);

    const summarize = (items) => {
        if (!items.length) {
            return { value: null, count: 0 };
        }
        const total = items.reduce((sum, item) => sum + item.average, 0);
        return { value: total / items.length, count: items.length };
    };

    return {
        all: summarize(evaluations),
        critical: summarize(evaluations.filter((item) => item.isCritical)),
        nonCritical: summarize(evaluations.filter((item) => !item.isCritical)),
    };
};

const formatAverage = (value) => {
    const num = toFiniteNumber(value);
    return num === null ? "—" : `${num.toFixed(2)}%`;
};

const formatEvaluationCount = (count) => `${count} avaliaç${count === 1 ? "ão" : "ões"}`;

// ---------- Modal (create/edit evaluation) ----------
const SupplierEvaluationModal = ({
    visible,
    onClose,
    supplier,   // { id, name, situation?, evaluation? }
    year,       // selectedYear (locked)
    weightConfigs,
    onSaved,
}) => {
    const existing = supplier?.evaluation || null;
    const defaultSituation =
        existing?.situation ?? supplier?.situation ?? "Fornecedor";

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        id: existing?.id ?? null,
        supplier: supplier?.id ?? null,
        year,
        situation: defaultSituation,
        critical: existing?.critical ?? supplier?.critical ?? false,
        evaluation_date: existing?.evaluation_date || todayISO(),
        price: Number.isFinite(existing?.price) ? existing.price : null,
        answer_capacity: Number.isFinite(existing?.answer_capacity) ? existing.answer_capacity : null,
        deadlines: Number.isFinite(existing?.deadlines) ? existing.deadlines : null,
        technical_capacity: Number.isFinite(existing?.technical_capacity) ? existing.technical_capacity : null,
        esg: Number.isFinite(existing?.esg) ? existing.esg : null,
        average: Number.isFinite(existing?.average) ? existing.average : null,
        qualification: existing?.qualification ?? "",
    });

    // Reset when modal/supplier/year changes
    useEffect(() => {
        const e = supplier?.evaluation || null;
        const sit = e?.situation ?? supplier?.situation ?? "Fornecedor";
        const averageNow = computeAveragePct(
            {
                price: Number.isFinite(e?.price) ? e.price : null,
                answer_capacity: Number.isFinite(e?.answer_capacity) ? e.answer_capacity : null,
                deadlines: Number.isFinite(e?.deadlines) ? e.deadlines : null,
                technical_capacity: Number.isFinite(e?.technical_capacity) ? e.technical_capacity : null,
                esg: Number.isFinite(e?.esg) ? e.esg : null,
            },
            year,
            weightConfigs
        );
        setForm({
            id: e?.id ?? null,
            supplier: supplier?.id ?? null,
            year,
            situation: sit,
            critical: e?.critical ?? supplier?.critical ?? false,
            evaluation_date: e?.evaluation_date || todayISO(),
            price: Number.isFinite(e?.price) ? e.price : null,
            answer_capacity: Number.isFinite(e?.answer_capacity) ? e.answer_capacity : null,
            deadlines: Number.isFinite(e?.deadlines) ? e.deadlines : null,
            technical_capacity: Number.isFinite(e?.technical_capacity) ? e.technical_capacity : null,
            esg: Number.isFinite(e?.esg) ? e.esg : null,
            average: Number.isFinite(averageNow) ? averageNow : null,
            qualification: computeQualification(sit, Number.isFinite(averageNow) ? averageNow : null),
        });
        setError(null);
    }, [supplier, year, visible, weightConfigs]);

    const onChangeInput = (field) => (e) => {
        const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [field]: v }));
    };
    const onChangeSelectNum = (field) => (e) => {
        const v = e.target.value === "" ? null : parseInt(e.target.value, 10);
        setForm((prev) => ({ ...prev, [field]: v }));
    };

    // Recompute average when score fields change
    useEffect(() => {
        const avg = computeAveragePct(
            {
                price: form.price,
                answer_capacity: form.answer_capacity,
                deadlines: form.deadlines,
                technical_capacity: form.technical_capacity,
                esg: form.esg,
            },
            year,
            weightConfigs
        );
        setForm((prev) => ({
            ...prev,
            average: avg,
        }));
    }, [form.price, form.answer_capacity, form.deadlines, form.technical_capacity, form.esg, year, weightConfigs]);

    // Recompute qualification when situation or average changes
    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            qualification: computeQualification(prev.situation, prev.average),
        }));
    }, [form.situation, form.average]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.supplier) {
            setError("Missing supplier id");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const resolvedConfig = getWeightConfig(weightConfigs, year);
            const showEsgField = (Number(resolvedConfig?.esg) || 0) > 0;
            const payload = {
                ...form,
                year, // enforce selectedYear
                esg: showEsgField ? form.esg : null,
            };

            // clean numeric fields
            ["price", "answer_capacity", "deadlines", "technical_capacity", "esg", "average"].forEach((k) => {
                if (payload[k] === "") payload[k] = null;
                if (typeof payload[k] === "string" && payload[k] !== "") payload[k] = Number(payload[k]);
            });

            if (payload.id) {
                await apiClient.put(`/supplier-evaluations/${payload.id}/`, payload);
            } else {
                const { id, ...createPayload } = payload;
                await apiClient.post(`/supplier-evaluations/`, createPayload);
            }
            onSaved?.();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || "Erro ao guardar";
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const showEsg = (Number(getWeightConfig(weightConfigs, year)?.esg) || 0) > 0;

    return (
        <CModal visible={visible} onClose={onClose} alignment="center" size="lg" backdrop="static">
            <CModalHeader>
                <CModalTitle>
                    {form.id ? `Editar avaliação – ${supplier?.name}` : `Nova avaliação – ${supplier?.name}`}
                </CModalTitle>
            </CModalHeader>
            <CForm onSubmit={onSubmit}>
                <CModalBody>
                    {error && <CAlert color="danger" className="mb-3">{error}</CAlert>}

                    <CRow className="g-3">
                        <CCol md={3}>
                            <label className="form-label fw-bold">Ano</label>
                            <CFormInput type="number" value={year} disabled readOnly />
                        </CCol>

                        <CCol md={4}>
                            <label className="form-label fw-bold">Data avaliação</label>
                            <CFormInput
                                type="date"
                                value={form.evaluation_date || ""}
                                onChange={onChangeInput("evaluation_date")}
                            />
                        </CCol>

                        <CCol md={12}>
                            <label className="form-label fw-bold">Situação</label>
                            <select
                                className="form-select"
                                value={form.situation}
                                onChange={onChangeInput("situation")}
                            >
                                {SITUATION_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </CCol>

                        <CCol md={12} className="d-flex align-items-end">
                            <CFormCheck
                                id="critical"
                                label={<strong>Crítico?</strong>}
                                checked={!!form.critical}
                                onChange={onChangeInput("critical")}
                                reverse
                            />
                        </CCol>

                        <CCol md={3}>
                            <label className="form-label fw-bold">Preço</label>
                            <select className="form-select" value={form.price ?? ""} onChange={onChangeSelectNum("price")}>
                                <option value="">—</option>
                                {SCORE_OPTIONS.map(([label, val]) => (
                                    <option key={`price-${val}`} value={val}>{label}</option>
                                ))}
                            </select>
                        </CCol>

                        <CCol md={3}>
                            <label className="form-label fw-bold">Cap. Resposta</label>
                            <select className="form-select" value={form.answer_capacity ?? ""} onChange={onChangeSelectNum("answer_capacity")}>
                                <option value="">—</option>
                                {SCORE_OPTIONS.map(([label, val]) => (
                                    <option key={`ans-${val}`} value={val}>{label}</option>
                                ))}
                            </select>
                        </CCol>

                        <CCol md={3}>
                            <label className="form-label fw-bold">Prazos</label>
                            <select className="form-select" value={form.deadlines ?? ""} onChange={onChangeSelectNum("deadlines")}>
                                <option value="">—</option>
                                {SCORE_OPTIONS.map(([label, val]) => (
                                    <option key={`dead-${val}`} value={val}>{label}</option>
                                ))}
                            </select>
                        </CCol>

                        <CCol md={3}>
                            <label className="form-label fw-bold">Cap. Técnica</label>
                            <select className="form-select" value={form.technical_capacity ?? ""} onChange={onChangeSelectNum("technical_capacity")}>
                                <option value="">—</option>
                                {SCORE_OPTIONS.map(([label, val]) => (
                                    <option key={`tech-${val}`} value={val}>{label}</option>
                                ))}
                            </select>
                        </CCol>

                        {showEsg && (
                            <CCol md={3}>
                                <label className="form-label fw-bold">ESG</label>
                                <select className="form-select" value={form.esg ?? ""} onChange={onChangeSelectNum("esg")}>
                                    <option value="">—</option>
                                    {SCORE_OPTIONS.map(([label, val]) => (
                                        <option key={`esg-${val}`} value={val}>{label}</option>
                                    ))}
                                </select>
                            </CCol>
                        )}

                        <CCol md={3}>
                            <label className="form-label fw-bold">Média (%)</label>
                            <CFormInput className="auto-field" type="number" value={form.average ?? ""} readOnly />
                        </CCol>

                        <CCol md={9}>
                            <label className="form-label fw-bold">Qualificação</label>
                            <CFormInput className="auto-field" value={form.qualification ?? ""} readOnly />
                        </CCol>
                    </CRow>
                </CModalBody>

                <CModalFooter>
                    <CButton color="secondary" variant="ghost" onClick={onClose} disabled={saving}>
                        Cancelar
                    </CButton>
                    <CButton color="primary" type="submit" disabled={saving}>
                        {saving ? 'A guardar…' : 'Guardar'}
                    </CButton>
                </CModalFooter>
            </CForm>
        </CModal>
    );
};


// ---------- Page ----------
const SuppliersEvaluation = () => {
    const [suppliers, setSuppliersEvaluation] = useState([]);
    const location = useLocation();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() - 1);
    const [availableYears, setAvailableYears] = useState([]);
    const [weightConfigs, setWeightConfigs] = useState([]);

    const fetchWeightConfigs = useCallback(async () => {
        try {
            const response = await apiClient.get("/supplier-evaluation-weights/");
            setWeightConfigs(response.data || []);
        } catch (error) {
            console.error("Error fetching supplier evaluation weights:", error);
        }
    }, []);

    const fetchSuppliersEvaluation = useCallback(async (year = selectedYear) => {
        try {
            const response = await apiClient.get(`/supplier-evaluations/?year=${year}`);
            setSuppliersEvaluation(response.data.suppliers);
            setAvailableYears(response.data.available_years);
        } catch (error) {
            console.error('Error fetching suppliers-evaluation:', error);
        }
    }, [selectedYear]);

    useEffect(() => {
        fetchSuppliersEvaluation(selectedYear);
    }, [location.pathname, selectedYear, fetchSuppliersEvaluation]);

    useEffect(() => {
        fetchWeightConfigs();
    }, [fetchWeightConfigs]);

    const averages = useMemo(() => computeYearAverages(suppliers), [suppliers]);

    // modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [activeSupplier, setActiveSupplier] = useState(null);

    const openModalForSupplier = (supplierRow) => {
        setActiveSupplier(supplierRow);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setActiveSupplier(null);
    };

    const columns = [
        // keep selector for sorting, but render a clickable cell
        {
            name: "Fornecedor",
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => openModalForSupplier(row)}
                >
                    {row.name}
                </button>
            ),
        },
        { name: "Categoria", selector: (row) => row.category, sortable: true },
        { name: "Média (%)", selector: (row) => row.evaluation?.average, sortable: true },
        { name: "Qualificação", selector: (row) => row.evaluation?.qualification, sortable: true },
        { name: "Data avaliação", selector: (row) => row.evaluation?.evaluation_date, sortable: true }
    ];

    return (
        <>
            <AlertNotification />
            <CRow className="g-3 mb-4">
                <CCol sm={6} lg={3}>
                    <CCard className="surveillance-kpi kpi-total h-100 shadow-sm">
                        <CCardBody className="py-3">
                            <div className="text-uppercase text-medium-emphasis small">
                                Ano
                            </div>
                            <div className="fs-4 fw-semibold">{selectedYear}</div>
                            <div className="text-medium-emphasis small">
                                Período selecionado
                            </div>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol sm={6} lg={3}>
                    <CCard className="surveillance-kpi kpi-completion h-100 shadow-sm">
                        <CCardBody className="py-3">
                            <div className="text-uppercase text-medium-emphasis small">
                                Média (todos)
                            </div>
                            <div className="fs-4 fw-semibold">
                                {formatAverage(averages.all.value)}
                            </div>
                            <div className="text-medium-emphasis small">
                                {formatEvaluationCount(averages.all.count)}
                            </div>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol sm={6} lg={3}>
                    <CCard className="surveillance-kpi kpi-overdue h-100 shadow-sm">
                        <CCardBody className="py-3">
                            <div className="text-uppercase text-medium-emphasis small">
                                Média (críticos)
                            </div>
                            <div className="fs-4 fw-semibold">
                                {formatAverage(averages.critical.value)}
                            </div>
                            <div className="text-medium-emphasis small">
                                {formatEvaluationCount(averages.critical.count)}
                            </div>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol sm={6} lg={3}>
                    <CCard className="surveillance-kpi kpi-pending h-100 shadow-sm">
                        <CCardBody className="py-3">
                            <div className="text-uppercase text-medium-emphasis small">
                                Média (não críticos)
                            </div>
                            <div className="fs-4 fw-semibold">
                                {formatAverage(averages.nonCritical.value)}
                            </div>
                            <div className="text-medium-emphasis small">
                                {formatEvaluationCount(averages.nonCritical.count)}
                            </div>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>
            <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">Avaliação de Fornecedores</h3>
                </CCardHeader>
                <CCardBody>
                    <DataTableComponent
                        columns={columns}
                        data={suppliers}
                        years={availableYears}
                        selectedYear={selectedYear}
                        onYearChange={(newYear) => setSelectedYear(newYear)}
                    />
                </CCardBody>
            </CCard>

            {/* Modal */}
            <SupplierEvaluationModal
                visible={modalOpen}
                onClose={closeModal}
                supplier={activeSupplier}
                year={selectedYear}
                weightConfigs={weightConfigs}
                onSaved={() => fetchSuppliersEvaluation(selectedYear)}
            />
        </>
    );
};

export default SuppliersEvaluation;
